import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { ArrowRight, ArrowLeft } from "../../../svg";
import { getStories } from "../../../functions/story";
import { getActiveLives } from "../../../functions/live";
import StoryViewer from "./StoryViewer";
import CreateStory from "./CreateStory";
import { useNavigate } from "react-router-dom";
import "./style.css";

export default function Stories() {
  const { user } = useSelector((state) => ({ ...state }));
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [myStories, setMyStories] = useState(null);
  const [lives, setLives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showViewer, setShowViewer] = useState(false);
  const [viewerData, setViewerData] = useState(null);
  const [showCreateStory, setShowCreateStory] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetchStories();
    fetchLives();
  }, []);

  useEffect(() => {
    checkScrollArrows();
    const scrollEl = scrollRef.current;
    if (scrollEl) {
      scrollEl.addEventListener("scroll", checkScrollArrows);
      return () => scrollEl.removeEventListener("scroll", checkScrollArrows);
    }
  }, [stories, myStories, lives]);

  const checkScrollArrows = () => {
    const el = scrollRef.current;
    if (el) {
      setShowLeftArrow(el.scrollLeft > 0);
      setShowRightArrow(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
    }
  };

  const fetchStories = async () => {
    setLoading(true);
    try {
      const res = await getStories(user.token);
      if (res.status === "ok") {
        setMyStories(res.data.myStories);
        setStories(res.data.stories || []);
      }
    } catch (err) {
      console.error("Error fetching stories:", err);
    }
    setLoading(false);
  };

  const fetchLives = async () => {
    try {
      const res = await getActiveLives(user.token);
      if (res.status === "ok") {
        setLives(res.data || []);
      }
    } catch (err) {
      console.error("Error fetching lives:", err);
    }
  };

  const openStoryViewer = (userStories, userIndex) => {
    setViewerData({
      initialUserIndex: userIndex,
      initialStoryIndex: 0,
    });
    setShowViewer(true);
  };

  const closeStoryViewer = () => {
    setShowViewer(false);
    setViewerData(null);
    fetchStories();
  };

  const handleStoryCreated = () => {
    setShowCreateStory(false);
    fetchStories();
  };

  const handleJoinLive = (live) => {
    navigate(`/live/${live._id}`);
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === "left" ? -200 : 200;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  // Build all stories array with myStories first
  const allUsersStories = myStories?.stories?.length > 0
    ? [myStories, ...stories]
    : stories;

  return (
    <div className="stories_bar">
      <div className="stories_bar_wrapper">
        {showLeftArrow && (
          <button className="stories_nav stories_nav_left" onClick={() => scroll("left")}>
            <ArrowLeft color="#fff" />
          </button>
        )}

        <div className="stories_bar_scroll" ref={scrollRef}>
          {/* Create Story Circle */}
          <div className="story_circle_item" onClick={() => setShowCreateStory(true)}>
            <div className="story_circle_create">
              <img src={user.picture} alt="" className="story_circle_avatar" />
              <div className="story_circle_plus">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 4v16m-8-8h16" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" />
                </svg>
              </div>
            </div>
            <span className="story_circle_name">Seu story</span>
          </div>

          {/* Active Lives - Show first with special LIVE badge */}
          {lives.map((live) => (
            <div
              key={`live-${live._id}`}
              className="story_circle_item live_circle_item"
              onClick={() => handleJoinLive(live)}
            >
              <div className="story_circle_ring live_ring">
                <div className="story_circle_inner">
                  <img
                    src={live.host?.picture}
                    alt={live.host?.first_name}
                    className="story_circle_avatar"
                  />
                </div>
                <div className="live_badge_circle">
                  <span>LIVE</span>
                </div>
                <div className="live_viewers_count">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="10" height="10">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                  </svg>
                  <span>{live.currentViewers || 0}</span>
                </div>
              </div>
              <span className="story_circle_name">{live.host?.first_name}</span>
            </div>
          ))}

          {/* Loading Skeleton */}
          {loading && (
            <>
              {[...Array(6)].map((_, i) => (
                <div key={i} className="story_circle_item">
                  <div className="story_circle_skeleton">
                    <div className="skeleton_ring"></div>
                  </div>
                  <div className="skeleton_name"></div>
                </div>
              ))}
            </>
          )}

          {/* My Stories */}
          {!loading && myStories?.stories?.length > 0 && (
            <div
              className="story_circle_item"
              onClick={() => openStoryViewer(myStories, 0)}
            >
              <div className={`story_circle_ring ${myStories.hasUnviewed ? "unviewed" : "viewed"}`}>
                <div className="story_circle_inner">
                  <img
                    src={myStories.user.picture}
                    alt={myStories.user.first_name}
                    className="story_circle_avatar"
                  />
                </div>
                {myStories.stories.length > 1 && (
                  <div className="story_count_indicator">
                    <span>{myStories.stories.length}</span>
                  </div>
                )}
              </div>
              <span className="story_circle_name">Seu story</span>
            </div>
          )}

          {/* Friends' Stories */}
          {!loading && stories.map((userStories, index) => (
            <div
              key={userStories.user._id}
              className="story_circle_item"
              onClick={() => openStoryViewer(userStories, myStories?.stories?.length > 0 ? index + 1 : index)}
            >
              <div className={`story_circle_ring ${userStories.hasUnviewed ? "unviewed" : "viewed"}`}>
                <div className="story_circle_inner">
                  <img
                    src={userStories.user.picture}
                    alt={userStories.user.first_name}
                    className="story_circle_avatar"
                  />
                </div>
                {userStories.stories.length > 1 && (
                  <div className="story_count_indicator">
                    <span>{userStories.stories.length}</span>
                  </div>
                )}
              </div>
              <span className="story_circle_name">{userStories.user.first_name}</span>
            </div>
          ))}

          {/* Empty state placeholder */}
          {!loading && stories.length === 0 && !myStories?.stories?.length && lives.length === 0 && (
            <div className="stories_empty_hint">
              <span>Nenhum story ou live no momento</span>
            </div>
          )}
        </div>

        {showRightArrow && (
          <button className="stories_nav stories_nav_right" onClick={() => scroll("right")}>
            <ArrowRight color="#fff" />
          </button>
        )}
      </div>

      {/* Story Viewer Modal */}
      {showViewer && viewerData && (
        <StoryViewer
          allUsersStories={allUsersStories}
          initialUserIndex={viewerData.initialUserIndex}
          initialStoryIndex={viewerData.initialStoryIndex}
          currentUserId={user.id}
          token={user.token}
          onClose={closeStoryViewer}
        />
      )}

      {/* Create Story Modal */}
      {showCreateStory && (
        <CreateStory
          user={user}
          onClose={() => setShowCreateStory(false)}
          onCreated={handleStoryCreated}
        />
      )}
    </div>
  );
}
